/* eslint-disable max-len */
import React, { Component } from 'react';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import { Button } from 'primereact/button';
import * as qs from 'query-string';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import {
  intlShape,
  growlShape,
  locationShape,
  historyShape,
} from '../components/prop-types';
import { UserAgreementCheckbox } from '../components/AgreementCheckboxes';
import { withGrowl } from '../components/GrowlNotifications';
import { LegacyInputTextComponent, LegacyInputPasswordComponent, DropdownWithValidation } from '../components/prime-formik';
import { validationMessages } from '../i18n/common';
import apollo from '../services/apollo';
import { useLocale as use_locale } from '../reducers/locale';
import { saveViewerLocale } from '../services/locale/ViewerLocale';
import { compare } from '../utils/AppUtil';

const mapState = (state) => {
  const { cldr } = state.i18n;
  return {
    cldr,
  };
};

const mapDispatchToProps = dispatch => ({
  updateMessages: selectedLanguage => dispatch(use_locale(selectedLanguage)),
});

const REGISTER_USER = gql`
  mutation($input: RegisterUserInput!) {
    register(input: $input) {
        createdUserId
    }
  }
`;

const registerUser = input => apollo
  .mutate({
    mutation: REGISTER_USER,
    variables: {
      input,
    },
  })
  .then(res => res.data);

const LOCALES_QUERY = gql`
  query activeLocales {
    locales: activeLocales
  }
`;

const COUNTRIES_QUERY = gql`
  query availableCountries {
    countries: availableCountries
  }
`;

const TIME_ZONES_QUERY = gql`
  query availableTimeZones {
    timeZones: availableTimeZones
  }
`;

const i18n = defineMessages({
  firstNameRequired: { id: 'register.first_name_required', defaultMessage: 'First name is required' },
  lastNameRequired: { id: 'register.last_name_required', defaultMessage: 'Last name is required' },
  emailRequired: { id: 'register.email_required', defaultMessage: 'Email is required' },
  passwordRequired: {
    id: 'login.password_required',
    defaultMessage: 'Password is required',
  },
  passwordTooShort: {
    id: 'password_validation.minimum_length',
    defaultMessage: 'Password must be at least 12 characters long',
  },
  passwordTooLong: {
    id: 'password_validation.maximum_length',
    defaultMessage: 'Invalid password',
  },
  passwordSameAsUsername: {
    id: 'password_validation.same_as_username',
    defaultMessage: 'Password cannot be the same as the username',
  },
  passwordConfirmationRequired: {
    id: 'login.password_confirmation_required',
    defaultMessage: 'Password confirmation is required',
  },
  passwordConfirmationDoesNotMatch: {
    id: 'login.password_confirmation_does_not_match',
    defaultMessage: 'Password confirmation does not match',
  },
  languageRequired: { id: 'register.language_required', defaultMessage: 'Language is required' },
  countryRequired: { id: 'register.country_required', defaultMessage: 'Country is required' },
  timeZoneRequired: { id: 'register.time_zone_required', defaultMessage: 'Time zone is required' },
  dropwdownPlaceholder: { id: 'register.dropdown.placeholder', defaultMessage: 'Choose' },
});

class Login extends Component {
  constructor(props) {
    super(props);

    this.state = {
      supportedLanguages: [],
      availableCountries: [],
      availableTimeZones: [],
      selectedLocale: 'en-US',
    };

    this.register = this.register.bind(this);
  }

  componentDidMount() {
    apollo.query({
      query: LOCALES_QUERY,
    }).then((res) => {
      const { cldr: { localeDisplayName } } = this.props;
      const supportedLanguages = res.data.locales.map(
        lang => ({
          value: lang,
          label: localeDisplayName(lang),
        }),
      );
      return this.setState({
        supportedLanguages,
      });
    });

    apollo.query({
      query: COUNTRIES_QUERY,
    }).then((res) => {
      const { cldr } = this.props;

      const { countries } = res.data;

      const availableCountries = cldr.teritoryDisplayNames(countries);

      return this.setState({
        availableCountries,
      });
    });

    apollo.query({
      query: TIME_ZONES_QUERY,
    }).then((res) => {
      const availableTimeZones = res.data.timeZones.map(
        timeZone => ({
          value: timeZone,
          label: timeZone,
        }),
      );
      return this.setState({
        availableTimeZones,
      });
    });
  }

  async register(values, resetForm) {
    const {
      growl,
      history,
    } = this.props;

    const {
      token, firstName, lastName, email, password, locale, country, timeZone,
    } = values;

    try {
      await registerUser({
        userInvitationToken: token,
        firstName,
        lastName,
        email,
        password,
        locale,
        country,
        timeZone,
      }).then((res) => {
        const { createdUserId } = res.register;

        history.push(`/register-confirmation?userId=${createdUserId}`);
      });
    } catch (error) {
      growl.show({ severity: 'error', detail: error.message, sticky: true });
      resetForm();
    }
  }


  render() {
    const {
      location,
      intl: { formatMessage },
      history,
      updateMessages,
    } = this.props;

    const { token } = qs.parse(location.search);

    const {
      supportedLanguages,
      availableTimeZones,
      availableCountries,
      selectedLocale,
    } = this.state;

    const initialValues = {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      locale: 'en-US',
      country: 'US',
      timeZone: 'GMT',
      client_terms_of_use_agreement: false,
      token,
    };

    const validationSchema = Yup.object().shape({
      firstName: Yup.string().required(formatMessage(i18n.firstNameRequired)),
      lastName: Yup.string().required(formatMessage(i18n.lastNameRequired)),
      email: Yup.string().required(formatMessage(i18n.emailRequired)),
      password: Yup.string()
        .when('email', {
          is: val => (!!(val && val.length > 0)),
          then: Yup.string().notOneOf(
            [Yup.ref('email')],
            formatMessage(i18n.passwordSameAsUsername),
          ),
        })
        .min(12, formatMessage(i18n.passwordTooShort))
        .max(100, formatMessage(i18n.passwordTooLong))
        .required(formatMessage(i18n.passwordRequired)),
      confirmPassword: Yup.string().when('password', {
        is: val => (!!(val && val.length > 0)),
        then: Yup.string().oneOf(
          [Yup.ref('password')],
          formatMessage(i18n.passwordConfirmationDoesNotMatch),
        ),
      }).required(formatMessage(i18n.passwordConfirmationRequired)),
      locale: Yup.string().required(formatMessage(i18n.languageRequired)),
      country: Yup.string().required(formatMessage(i18n.countryRequired)),
      timeZone: Yup.string().required(formatMessage(i18n.timeZoneRequired)),
      client_terms_of_use_agreement: Yup.boolean()
        .required(formatMessage(validationMessages.fieldMustBeChecked))
        .oneOf([true], formatMessage(validationMessages.fieldMustBeChecked))
        .default(false),
    });

    const sortedLanguages = supportedLanguages.sort(compare);

    const sortedTimeZones = availableTimeZones.sort(compare);

    const sortedCountries = availableCountries.sort(compare);

    const placeholder = formatMessage(i18n.dropwdownPlaceholder);

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, formikBag) => {
          const { resetForm } = formikBag;
          this.register(values, resetForm);
        }}
        render={props => (
          <Form onSubmit={props.handleSubmit}>
            <input type="hidden" name="token" value={token} />
            <div className="p-grid p-fluid p-justify-center logo-top">
              <img src="images/default-195.png" className="brand center-block" alt="GPI Logo" />
            </div>
            <br />
            <div className="p-grid p-fluid p-justify-center">
              <div className="p-col-12 p-md-3">
                <FormattedMessage id="register.first_name" defaultMessage="First name">
                  {text => <Field id="firstName" name="firstName" component={LegacyInputTextComponent} placeholder={text} />}
                </FormattedMessage>
              </div>
            </div>
            <br />
            <div className="p-grid p-fluid p-justify-center">
              <div className="p-col-12 p-md-3">
                <FormattedMessage id="register.last_name" defaultMessage="Last name">
                  {text => <Field id="lastName" name="lastName" component={LegacyInputTextComponent} placeholder={text} />}
                </FormattedMessage>
              </div>
            </div>
            <br />
            <div className="p-grid p-fluid p-justify-center">
              <div className="p-col-12 p-md-3">
                <FormattedMessage id="register.email" defaultMessage="Email">
                  {text => <Field id="email" name="email" component={LegacyInputTextComponent} placeholder={text} />}
                </FormattedMessage>
              </div>
            </div>
            <br />
            <div className="p-grid p-fluid p-justify-center">
              <div className="p-col-12 p-md-3">
                <FormattedMessage id="register.password" defaultMessage="Password">
                  {text => <Field id="password" name="password" component={LegacyInputPasswordComponent} placeholder={text} />}
                </FormattedMessage>
              </div>
            </div>
            <br />
            <div className="p-grid p-fluid p-justify-center">
              <div className="p-col-12 p-md-3">
                <FormattedMessage id="register.confirm_password" defaultMessage="Confirm password">
                  {text => <Field id="confirmPassword" name="confirmPassword" component={LegacyInputPasswordComponent} placeholder={text} />}
                </FormattedMessage>
              </div>
            </div>
            <br />
            <div className="p-grid p-fluid p-justify-center">
              <div className="p-col-12 p-md-3">
                <Field
                  id="locale"
                  name="locale"
                  component={DropdownWithValidation}
                  options={sortedLanguages}
                  placeholder={`-- ${placeholder} --`}
                  filter
                  filterBy="label,value"
                  onChange={(e) => {
                    const { value } = e;

                    props.setFieldValue('locale', value);
                    updateMessages(value);
                    saveViewerLocale(value);
                    this.setState({
                      selectedLocale: value,
                    });
                  }}
                />
              </div>
            </div>
            <br />
            <div className="p-grid p-fluid p-justify-center">
              <div className="p-col-12 p-md-3">
                <Field
                  id="country"
                  name="country"
                  component={DropdownWithValidation}
                  options={sortedCountries}
                  placeholder={`-- ${placeholder} --`}
                  filter
                  filterBy="label,value"
                />
              </div>
            </div>
            <br />
            <div className="p-grid p-fluid p-justify-center">
              <div className="p-col-12 p-md-3">
                <Field
                  id="timeZone"
                  name="timeZone"
                  component={DropdownWithValidation}
                  options={sortedTimeZones}
                  placeholder={`-- ${placeholder} --`}
                />
              </div>
            </div>
            <br />
            <div className="p-grid p-fluid p-justify-center">
              <div className="p-col-12 p-md-3">
                <Field
                  id="client_terms_of_use_agreement"
                  name="client_terms_of_use_agreement"
                  {...props}
                  locale={selectedLocale}
                  component={UserAgreementCheckbox}
                />
              </div>
            </div>
            <br />
            <div className="p-grid p-fluid p-justify-center">
              <div className="p-col-6 p-md-1">
                <FormattedMessage id="register.button" defaultMessage="Register">
                  {text => (
                    <Button
                      type="submit"
                      label={text}
                      disabled={props.isSubmitting}
                    />
                  )}
                </FormattedMessage>
              </div>
              <div className="p-col-6 p-md-1">
                <FormattedMessage id="register.button_decline" defaultMessage="Decline">
                  {text => (
                    <Button
                      className="p-button-danger"
                      label={text}
                      onClick={() => history.push(`/user-invitation-decline?token=${token}`)}
                    />
                  )}
                </FormattedMessage>
              </div>
            </div>
          </Form>
        )}
      />
    );
  }
}

Login.propTypes = {
  intl: intlShape.isRequired,
  growl: growlShape.isRequired,
  location: locationShape.isRequired,
  history: historyShape.isRequired,
  cldr: PropTypes.objectOf(PropTypes.any).isRequired,
  updateMessages: PropTypes.func.isRequired,
};

export default connect(mapState, mapDispatchToProps)(injectIntl(withGrowl(Login)));
